/**
 * TUVI - Módulo Contabilidad
 * Dashboard financiero, retenciones, cuentas por pagar/cobrar, caja menor
 */

import React, { useState, useCallback, useEffect } from 'react';
import client from '../../services/graphqlClient';
import { UPLOAD_URL } from '../../config';
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';
import * as XLSX from 'xlsx';
import './Contabilidad.css';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const formatMoney = v => new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', minimumFractionDigits: 0 }).format(v || 0);
const formatDate = d => !d ? '-' : new Date(d + 'T12:00:00').toLocaleDateString('es-CO', { day: '2-digit', month: 'short', year: 'numeric' });

const todayStr = () => new Date().toISOString().split('T')[0];
const firstDayOfMonth = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`; };
const firstDayOfYear = () => `${new Date().getFullYear()}-01-01`;

const COLORS_PIE = ['#6366f1', '#f59e0b', '#ef4444', '#10b981', '#3b82f6', '#8b5cf6'];
const ESTADO_LABELS = { pendiente: 'Pendiente', pagado_parcial: 'Parcial', pagado: 'Pagado', vencido: 'Vencida', cancelado: 'Cancelada' };
const ESTADO_COLORS = { pendiente: '#f59e0b', pagado_parcial: '#3b82f6', pagado: '#10b981', vencido: '#ef4444', cancelado: '#9ca3af' };

function exportToExcel(data, columns, filename) {
  const headers = columns.map(c => c.header);
  const rows = data.map(row => columns.map(c => c.value(row)));
  const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
  ws['!cols'] = columns.map(c => ({ wch: c.width || 15 }));
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Datos');
  XLSX.writeFile(wb, `${filename}.xlsx`);
}

// ═══════════════════════════════════════════════════════════════════════════════
// COMPONENTE PRINCIPAL
// ═══════════════════════════════════════════════════════════════════════════════

export default function Contabilidad() {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [fechaDesde, setFechaDesde] = useState(firstDayOfYear());
  const [fechaHasta, setFechaHasta] = useState(todayStr());

  const TABS = [
    { key: 'dashboard',    label: 'Dashboard' },
    { key: 'retenciones',  label: 'Retenciones' },
    { key: 'por_pagar',    label: 'Cuentas por Pagar' },
    { key: 'por_cobrar',   label: 'Cuentas por Cobrar' },
    { key: 'caja_menor',   label: 'Caja Menor' },
  ];

  return (
    <div className="cont-container">
      <div className="cont-header">
        <div>
          <h2>Contabilidad</h2>
          <p className="cont-subtitle">Panel financiero y contable</p>
        </div>
        <div className="cont-fecha-global">
          <label>Desde</label>
          <input type="date" value={fechaDesde} onChange={e => setFechaDesde(e.target.value)} />
          <label>Hasta</label>
          <input type="date" value={fechaHasta} onChange={e => setFechaHasta(e.target.value)} />
        </div>
      </div>

      <div className="cont-tabs">
        {TABS.map(t => (
          <button key={t.key} className={`cont-tab${activeTab === t.key ? ' active' : ''}`} onClick={() => setActiveTab(t.key)}>
            {t.label}
          </button>
        ))}
      </div>

      {activeTab === 'dashboard' && <TabDashboard fechaDesde={fechaDesde} fechaHasta={fechaHasta} />}
      {activeTab === 'retenciones' && <TabRetenciones fechaDesde={fechaDesde} fechaHasta={fechaHasta} />}
      {activeTab === 'por_pagar' && <TabCuentasPorPagar fechaDesde={fechaDesde} fechaHasta={fechaHasta} />}
      {activeTab === 'por_cobrar' && <TabCuentasPorCobrar />}
      {activeTab === 'caja_menor' && <TabCajaMenor fechaDesde={fechaDesde} fechaHasta={fechaHasta} />}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// TAB 1: DASHBOARD
// ═══════════════════════════════════════════════════════════════════════════════

function TabDashboard({ fechaDesde, fechaHasta }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const r = await client.query(
        `query($fd: String!, $fh: String!) {
          contabilidadDashboard(fecha_desde: $fd, fecha_hasta: $fh) {
            total_facturado total_pagado_proveedores saldo_pendiente_proveedores
            total_retenciones retenciones_fuente retenciones_iva retenciones_otras
            total_cuentas_cobro total_recaudado_cartera
            caja_menor_entregada caja_menor_gastada
            facturas_por_estado { estado cantidad monto }
            flujo_mensual { mes ingresos egresos }
            retenciones_por_tipo { tipo total }
          }
        }`,
        { fd: fechaDesde, fh: fechaHasta }
      );
      setData(r.contabilidadDashboard);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [fechaDesde, fechaHasta]);

  useEffect(() => { cargar(); }, [cargar]);

  if (loading) return <div className="cont-loading">Cargando dashboard...</div>;
  if (!data) return <div className="cont-empty">Seleccione un periodo y presione Filtrar</div>;

  const d = data;

  return (
    <div>
      {/* KPIs */}
      <div className="cont-kpi-grid">
        <div className="cont-kpi"><div className="cont-kpi-value">{formatMoney(d.total_facturado)}</div><div className="cont-kpi-label">Total Facturado</div></div>
        <div className="cont-kpi"><div className="cont-kpi-value" style={{ color: '#059669' }}>{formatMoney(d.total_pagado_proveedores)}</div><div className="cont-kpi-label">Pagado a Proveedores</div></div>
        <div className="cont-kpi"><div className="cont-kpi-value" style={{ color: '#dc2626' }}>{formatMoney(d.saldo_pendiente_proveedores)}</div><div className="cont-kpi-label">Saldo Pendiente Proveedores</div></div>
        <div className="cont-kpi" style={{ borderTop: '3px solid #6366f1' }}><div className="cont-kpi-value" style={{ color: '#6366f1' }}>{formatMoney(d.total_retenciones)}</div><div className="cont-kpi-label">Total Retenciones</div></div>
      </div>
      <div className="cont-kpi-grid">
        <div className="cont-kpi"><div className="cont-kpi-value">{formatMoney(d.total_cuentas_cobro)}</div><div className="cont-kpi-label">Cuentas de Cobro Emitidas</div></div>
        <div className="cont-kpi"><div className="cont-kpi-value" style={{ color: '#059669' }}>{formatMoney(d.total_recaudado_cartera)}</div><div className="cont-kpi-label">Recaudado (Cartera)</div></div>
        <div className="cont-kpi"><div className="cont-kpi-value">{formatMoney(d.caja_menor_entregada)}</div><div className="cont-kpi-label">Caja Menor Entregada</div></div>
        <div className="cont-kpi"><div className="cont-kpi-value">{formatMoney(d.caja_menor_gastada)}</div><div className="cont-kpi-label">Caja Menor Gastada</div></div>
      </div>

      <div className="cont-charts-row">
        {/* Flujo de caja mensual */}
        <div className="cont-chart-box" style={{ flex: 2 }}>
          <h4>Flujo de Caja Mensual</h4>
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={d.flujo_mensual}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="mes" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} tickFormatter={v => `$${(v / 1e6).toFixed(0)}M`} />
              <Tooltip formatter={v => formatMoney(v)} />
              <Legend />
              <Area type="monotone" dataKey="ingresos" stroke="#10b981" fill="#d1fae5" name="Ingresos" />
              <Area type="monotone" dataKey="egresos" stroke="#ef4444" fill="#fee2e2" name="Egresos" />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Retenciones por tipo */}
        <div className="cont-chart-box" style={{ flex: 1 }}>
          <h4>Retenciones por Tipo</h4>
          {d.retenciones_por_tipo.length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie data={d.retenciones_por_tipo} dataKey="total" nameKey="tipo" cx="50%" cy="50%" outerRadius={90} label={({ tipo, total }) => `${formatMoney(total)}`}>
                  {d.retenciones_por_tipo.map((_, i) => <Cell key={i} fill={COLORS_PIE[i % COLORS_PIE.length]} />)}
                </Pie>
                <Tooltip formatter={v => formatMoney(v)} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          ) : <div className="cont-empty">Sin retenciones en el periodo</div>}
        </div>
      </div>

      {/* Facturas por estado */}
      <div className="cont-chart-box">
        <h4>Facturas por Estado</h4>
        <div className="cont-estados-bar">
          {d.facturas_por_estado.map(e => (
            <div key={e.estado} className="cont-estado-item">
              <div className="cont-estado-dot" style={{ background: ESTADO_COLORS[e.estado] || '#94a3b8' }} />
              <span className="cont-estado-label">{ESTADO_LABELS[e.estado] || e.estado}</span>
              <span className="cont-estado-count">{e.cantidad}</span>
              <span className="cont-estado-monto">{formatMoney(e.monto)}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// TAB 2: RETENCIONES
// ═══════════════════════════════════════════════════════════════════════════════

function TabRetenciones({ fechaDesde, fechaHasta }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filtroTipo, setFiltroTipo] = useState('');

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const r = await client.query(
        `query($fd: String!, $fh: String!, $tipo: String) {
          contabilidadRetenciones(fecha_desde: $fd, fecha_hasta: $fh, tipo_retencion: $tipo) {
            items { factura_id numero_factura proveedor_nombre proveedor_nit proyecto_nombre apartamento fecha_emision subtotal iva retencion_fuente retencion_iva otras_retenciones total_retenciones valor_neto estado }
            totales { subtotal iva retencion_fuente retencion_iva otras_retenciones total_retenciones valor_neto cantidad_facturas }
          }
        }`,
        { fd: fechaDesde, fh: fechaHasta, tipo: filtroTipo || null }
      );
      setData(r.contabilidadRetenciones);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [fechaDesde, fechaHasta, filtroTipo]);

  useEffect(() => { cargar(); }, [cargar]);

  const handleExport = () => {
    if (!data?.items) return;
    exportToExcel(data.items, [
      { header: '# Factura', value: r => r.numero_factura, width: 14 },
      { header: 'Proveedor', value: r => r.proveedor_nombre, width: 30 },
      { header: 'NIT', value: r => r.proveedor_nit, width: 15 },
      { header: 'Proyecto', value: r => r.proyecto_nombre, width: 20 },
      { header: 'Apto', value: r => r.apartamento, width: 10 },
      { header: 'Fecha Emisión', value: r => r.fecha_emision, width: 14 },
      { header: 'Subtotal', value: r => r.subtotal, width: 15 },
      { header: 'IVA', value: r => r.iva, width: 12 },
      { header: 'Ret. Fuente', value: r => r.retencion_fuente, width: 15 },
      { header: 'Ret. IVA', value: r => r.retencion_iva, width: 12 },
      { header: 'Otras Ret.', value: r => r.otras_retenciones, width: 12 },
      { header: 'Total Retenciones', value: r => r.total_retenciones, width: 16 },
      { header: 'Valor Neto', value: r => r.valor_neto, width: 15 },
      { header: 'Estado', value: r => r.estado, width: 12 },
    ], `Retenciones_${fechaDesde}_${fechaHasta}`);
  };

  return (
    <div>
      <div className="cont-filtros">
        <select value={filtroTipo} onChange={e => setFiltroTipo(e.target.value)}>
          <option value="">Todas las retenciones</option>
          <option value="fuente">Solo Ret. en Fuente</option>
          <option value="iva">Solo Ret. IVA</option>
          <option value="otras">Solo Otras Ret.</option>
        </select>
        <button className="cont-btn cont-btn-export" onClick={handleExport} disabled={!data?.items?.length}>
          Exportar Excel
        </button>
      </div>

      {loading && <div className="cont-loading">Cargando retenciones...</div>}

      {!loading && data && (
        <>
          {/* Totales */}
          <div className="cont-totales-bar">
            <div><span>Facturas:</span> <strong>{data.totales.cantidad_facturas}</strong></div>
            <div><span>Subtotal:</span> <strong>{formatMoney(data.totales.subtotal)}</strong></div>
            <div style={{ color: '#6366f1' }}><span>Ret. Fuente:</span> <strong>{formatMoney(data.totales.retencion_fuente)}</strong></div>
            <div style={{ color: '#f59e0b' }}><span>Ret. IVA:</span> <strong>{formatMoney(data.totales.retencion_iva)}</strong></div>
            <div style={{ color: '#ef4444' }}><span>Total Ret.:</span> <strong>{formatMoney(data.totales.total_retenciones)}</strong></div>
            <div style={{ color: '#059669' }}><span>Neto:</span> <strong>{formatMoney(data.totales.valor_neto)}</strong></div>
          </div>

          {/* Tabla */}
          <div className="cont-table-wrap">
            <table className="cont-table">
              <thead>
                <tr>
                  <th># Factura</th><th>Proveedor</th><th>NIT</th><th>Proyecto</th><th>Fecha</th>
                  <th>Subtotal</th><th>Ret. Fuente</th><th>Ret. IVA</th><th>Otras</th><th>Total Ret.</th><th>Neto</th>
                </tr>
              </thead>
              <tbody>
                {data.items.length === 0 ? (
                  <tr><td colSpan={11} className="cont-empty-td">Sin retenciones en el periodo</td></tr>
                ) : data.items.map(r => (
                  <tr key={r.factura_id}>
                    <td><strong>{r.numero_factura}</strong></td>
                    <td>{r.proveedor_nombre}</td>
                    <td>{r.proveedor_nit}</td>
                    <td>{r.proyecto_nombre}{r.apartamento ? ` · ${r.apartamento}` : ''}</td>
                    <td>{formatDate(r.fecha_emision)}</td>
                    <td className="num">{formatMoney(r.subtotal)}</td>
                    <td className="num ret">{r.retencion_fuente > 0 ? formatMoney(r.retencion_fuente) : '-'}</td>
                    <td className="num ret">{r.retencion_iva > 0 ? formatMoney(r.retencion_iva) : '-'}</td>
                    <td className="num ret">{r.otras_retenciones > 0 ? formatMoney(r.otras_retenciones) : '-'}</td>
                    <td className="num ret-total">{formatMoney(r.total_retenciones)}</td>
                    <td className="num neto">{formatMoney(r.valor_neto)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// TAB 3: CUENTAS POR PAGAR
// ═══════════════════════════════════════════════════════════════════════════════

function TabCuentasPorPagar({ fechaDesde, fechaHasta }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filtroEstado, setFiltroEstado] = useState('');
  const [expandedId, setExpandedId] = useState(null);

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const r = await client.query(
        `query($fd: String, $fh: String, $est: String) {
          contabilidadCuentasPorPagar(fecha_desde: $fd, fecha_hasta: $fh, estado: $est) {
            items { factura_id numero_factura proveedor_nombre proveedor_nit proyecto_nombre apartamento fecha_emision fecha_vencimiento monto monto_pagado saldo_pendiente retencion_fuente retencion_iva otras_retenciones total_retenciones valor_neto estado dias_vencido pagos { id monto fecha metodo banco referencia } }
            aging { corriente { cantidad monto } dias_31_60 { cantidad monto } dias_61_90 { cantidad monto } dias_90_mas { cantidad monto } }
            totales { total_facturas monto_total monto_pagado saldo_pendiente total_retenciones }
          }
        }`,
        { fd: fechaDesde || null, fh: fechaHasta || null, est: filtroEstado || null }
      );
      setData(r.contabilidadCuentasPorPagar);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [fechaDesde, fechaHasta, filtroEstado]);

  useEffect(() => { cargar(); }, [cargar]);

  const handleExport = () => {
    if (!data?.items) return;
    exportToExcel(data.items, [
      { header: '# Factura', value: r => r.numero_factura, width: 14 },
      { header: 'Proveedor', value: r => r.proveedor_nombre, width: 30 },
      { header: 'NIT', value: r => r.proveedor_nit, width: 15 },
      { header: 'Proyecto', value: r => r.proyecto_nombre, width: 20 },
      { header: 'Apto', value: r => r.apartamento, width: 10 },
      { header: 'Emisión', value: r => r.fecha_emision, width: 14 },
      { header: 'Vencimiento', value: r => r.fecha_vencimiento, width: 14 },
      { header: 'Monto', value: r => r.monto, width: 15 },
      { header: 'Pagado', value: r => r.monto_pagado, width: 15 },
      { header: 'Saldo', value: r => r.saldo_pendiente, width: 15 },
      { header: 'Retenciones', value: r => r.total_retenciones, width: 15 },
      { header: 'Neto', value: r => r.valor_neto, width: 15 },
      { header: 'Estado', value: r => r.estado, width: 12 },
      { header: 'Días Vencido', value: r => r.dias_vencido, width: 12 },
    ], `CuentasPorPagar_${fechaDesde}_${fechaHasta}`);
  };

  return (
    <div>
      <div className="cont-filtros">
        <select value={filtroEstado} onChange={e => setFiltroEstado(e.target.value)}>
          <option value="">Todos los estados</option>
          <option value="pendiente">Pendiente</option>
          <option value="pagado_parcial">Parcial</option>
          <option value="pagado">Pagado</option>
          <option value="vencido">Vencido</option>
        </select>
        <button className="cont-btn cont-btn-export" onClick={handleExport} disabled={!data?.items?.length}>
          Exportar Excel
        </button>
      </div>

      {loading && <div className="cont-loading">Cargando...</div>}

      {!loading && data && (
        <>
          {/* Aging Report */}
          <div className="cont-aging-grid">
            <div className="cont-aging-box aging-ok"><div className="cont-aging-label">Corriente (0-30d)</div><div className="cont-aging-count">{data.aging.corriente.cantidad}</div><div className="cont-aging-monto">{formatMoney(data.aging.corriente.monto)}</div></div>
            <div className="cont-aging-box aging-warn"><div className="cont-aging-label">31-60 días</div><div className="cont-aging-count">{data.aging.dias_31_60.cantidad}</div><div className="cont-aging-monto">{formatMoney(data.aging.dias_31_60.monto)}</div></div>
            <div className="cont-aging-box aging-danger"><div className="cont-aging-label">61-90 días</div><div className="cont-aging-count">{data.aging.dias_61_90.cantidad}</div><div className="cont-aging-monto">{formatMoney(data.aging.dias_61_90.monto)}</div></div>
            <div className="cont-aging-box aging-critical"><div className="cont-aging-label">90+ días</div><div className="cont-aging-count">{data.aging.dias_90_mas.cantidad}</div><div className="cont-aging-monto">{formatMoney(data.aging.dias_90_mas.monto)}</div></div>
          </div>

          {/* Totales */}
          <div className="cont-totales-bar">
            <div><span>Facturas:</span> <strong>{data.totales.total_facturas}</strong></div>
            <div><span>Total:</span> <strong>{formatMoney(data.totales.monto_total)}</strong></div>
            <div style={{ color: '#059669' }}><span>Pagado:</span> <strong>{formatMoney(data.totales.monto_pagado)}</strong></div>
            <div style={{ color: '#dc2626' }}><span>Saldo:</span> <strong>{formatMoney(data.totales.saldo_pendiente)}</strong></div>
            <div style={{ color: '#6366f1' }}><span>Retenciones:</span> <strong>{formatMoney(data.totales.total_retenciones)}</strong></div>
          </div>

          {/* Tabla */}
          <div className="cont-table-wrap">
            <table className="cont-table">
              <thead>
                <tr>
                  <th># Factura</th><th>Proveedor</th><th>Proyecto</th><th>Emisión</th><th>Vencimiento</th>
                  <th>Monto</th><th>Pagado</th><th>Saldo</th><th>Ret.</th><th>Estado</th><th>Días</th>
                </tr>
              </thead>
              <tbody>
                {data.items.length === 0 ? (
                  <tr><td colSpan={11} className="cont-empty-td">Sin facturas para el filtro seleccionado</td></tr>
                ) : data.items.map(f => (
                  <React.Fragment key={f.factura_id}>
                    <tr className={`cont-row-${f.estado}`} onClick={() => setExpandedId(expandedId === f.factura_id ? null : f.factura_id)} style={{ cursor: f.pagos?.length ? 'pointer' : 'default' }}>
                      <td><strong>{f.numero_factura}</strong></td>
                      <td>{f.proveedor_nombre}</td>
                      <td>{f.proyecto_nombre}{f.apartamento ? ` · ${f.apartamento}` : ''}</td>
                      <td>{formatDate(f.fecha_emision)}</td>
                      <td>{formatDate(f.fecha_vencimiento)}</td>
                      <td className="num">{formatMoney(f.monto)}</td>
                      <td className="num">{formatMoney(f.monto_pagado)}</td>
                      <td className="num" style={{ color: '#dc2626', fontWeight: 600 }}>{formatMoney(f.saldo_pendiente)}</td>
                      <td className="num">{f.total_retenciones > 0 ? formatMoney(f.total_retenciones) : '-'}</td>
                      <td><span className="cont-badge" style={{ background: ESTADO_COLORS[f.estado] || '#94a3b8', color: '#fff' }}>{ESTADO_LABELS[f.estado] || f.estado}</span></td>
                      <td>{f.dias_vencido > 0 ? <span style={{ color: '#dc2626', fontWeight: 700 }}>{f.dias_vencido}d</span> : '-'}</td>
                    </tr>
                    {expandedId === f.factura_id && f.pagos?.length > 0 && (
                      <tr><td colSpan={11} style={{ padding: 0 }}>
                        <div className="cont-pagos-detalle">
                          <strong>Pagos realizados:</strong>
                          <table className="cont-table-inner">
                            <thead><tr><th>Fecha</th><th>Monto</th><th>Método</th><th>Banco</th><th>Referencia</th></tr></thead>
                            <tbody>
                              {f.pagos.map(p => (
                                <tr key={p.id}>
                                  <td>{formatDate(p.fecha)}</td>
                                  <td className="num">{formatMoney(p.monto)}</td>
                                  <td>{p.metodo || '-'}</td>
                                  <td>{p.banco || '-'}</td>
                                  <td>{p.referencia || '-'}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </td></tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// TAB 4: CUENTAS POR COBRAR
// ═══════════════════════════════════════════════════════════════════════════════

function TabCuentasPorCobrar() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expandedId, setExpandedId] = useState(null);

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const r = await client.query(
        `{ contabilidadCuentasPorCobrar {
            items { proyecto_id proyecto_nombre apartamento nombre_cliente valor_contrato total_cobrado pendiente porcentaje_avance cuentas_cobro_emitidas campos_pago { nombre valor_calculado valor_recibido estado_cobro } }
            totales { total_proyectos valor_contrato_total total_cobrado total_pendiente }
          } }`
      );
      setData(r.contabilidadCuentasPorCobrar);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const handleExport = () => {
    if (!data?.items) return;
    exportToExcel(data.items, [
      { header: 'Proyecto', value: r => r.proyecto_nombre, width: 25 },
      { header: 'Apto', value: r => r.apartamento, width: 10 },
      { header: 'Cliente', value: r => r.nombre_cliente, width: 25 },
      { header: 'Valor Contrato', value: r => r.valor_contrato, width: 18 },
      { header: 'Total Cobrado', value: r => r.total_cobrado, width: 18 },
      { header: 'Pendiente', value: r => r.pendiente, width: 18 },
      { header: '% Avance', value: r => r.porcentaje_avance, width: 10 },
      { header: 'CdC Emitidas', value: r => r.cuentas_cobro_emitidas, width: 12 },
    ], `CuentasPorCobrar`);
  };

  return (
    <div>
      <div className="cont-filtros">
        <button className="cont-btn cont-btn-export" onClick={handleExport} disabled={!data?.items?.length}>
          Exportar Excel
        </button>
      </div>

      {loading && <div className="cont-loading">Cargando...</div>}

      {!loading && data && (
        <>
          <div className="cont-totales-bar">
            <div><span>Proyectos:</span> <strong>{data.totales.total_proyectos}</strong></div>
            <div><span>Valor Contratos:</span> <strong>{formatMoney(data.totales.valor_contrato_total)}</strong></div>
            <div style={{ color: '#059669' }}><span>Cobrado:</span> <strong>{formatMoney(data.totales.total_cobrado)}</strong></div>
            <div style={{ color: '#dc2626' }}><span>Pendiente:</span> <strong>{formatMoney(data.totales.total_pendiente)}</strong></div>
          </div>

          <div className="cont-table-wrap">
            <table className="cont-table">
              <thead>
                <tr>
                  <th>Proyecto</th><th>Apto</th><th>Cliente</th><th>Valor Contrato</th>
                  <th>Cobrado</th><th>Pendiente</th><th>% Avance</th><th>CdC</th>
                </tr>
              </thead>
              <tbody>
                {data.items.length === 0 ? (
                  <tr><td colSpan={8} className="cont-empty-td">Sin datos de cartera</td></tr>
                ) : data.items.map(p => (
                  <React.Fragment key={p.proyecto_id}>
                    <tr onClick={() => setExpandedId(expandedId === p.proyecto_id ? null : p.proyecto_id)} style={{ cursor: 'pointer' }}>
                      <td><strong>{p.proyecto_nombre}</strong></td>
                      <td>{p.apartamento || '-'}</td>
                      <td>{p.nombre_cliente}</td>
                      <td className="num">{formatMoney(p.valor_contrato)}</td>
                      <td className="num" style={{ color: '#059669' }}>{formatMoney(p.total_cobrado)}</td>
                      <td className="num" style={{ color: '#dc2626' }}>{formatMoney(p.pendiente)}</td>
                      <td>
                        <div className="cont-progress-bar">
                          <div className="cont-progress-fill" style={{ width: `${Math.min(p.porcentaje_avance, 100)}%` }} />
                        </div>
                        <span className="cont-progress-text">{p.porcentaje_avance}%</span>
                      </td>
                      <td>{p.cuentas_cobro_emitidas}</td>
                    </tr>
                    {expandedId === p.proyecto_id && p.campos_pago?.length > 0 && (
                      <tr><td colSpan={8} style={{ padding: 0 }}>
                        <div className="cont-pagos-detalle">
                          <strong>Campos de pago:</strong>
                          <table className="cont-table-inner">
                            <thead><tr><th>Concepto</th><th>Esperado</th><th>Recibido</th><th>Estado Cobro</th></tr></thead>
                            <tbody>
                              {p.campos_pago.map((c, i) => (
                                <tr key={i}>
                                  <td>{c.nombre}</td>
                                  <td className="num">{formatMoney(c.valor_calculado)}</td>
                                  <td className="num" style={{ color: '#059669' }}>{formatMoney(c.valor_recibido)}</td>
                                  <td><span className={`cont-cobro-badge cont-cobro-${c.estado_cobro || 'sin_enviar'}`}>{c.estado_cobro || 'sin enviar'}</span></td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </td></tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// TAB 5: CAJA MENOR
// ═══════════════════════════════════════════════════════════════════════════════

function TabCajaMenor({ fechaDesde, fechaHasta }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expandedId, setExpandedId] = useState(null);

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const r = await client.query(
        `query($fd: String, $fh: String) {
          contabilidadCajaMenor(fecha_desde: $fd, fecha_hasta: $fh) {
            items { ingeniero_id ingeniero_nombre total_entregado total_comprobado total_aprobado pendiente_legalizar saldo entregas { id fecha valor descripcion } }
            totales { total_entregado total_comprobado total_aprobado pendiente_legalizar }
          }
        }`,
        { fd: fechaDesde || null, fh: fechaHasta || null }
      );
      setData(r.contabilidadCajaMenor);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [fechaDesde, fechaHasta]);

  useEffect(() => { cargar(); }, [cargar]);

  const handleExport = () => {
    if (!data?.items) return;
    // Exportar con detalle de entregas
    const rows = [];
    data.items.forEach(i => {
      if (i.entregas?.length) {
        i.entregas.forEach(e => {
          rows.push({ ingeniero: i.ingeniero_nombre, fecha_entrega: e.fecha, valor_entrega: e.valor, descripcion: e.descripcion, total_entregado: i.total_entregado, total_comprobado: i.total_comprobado, total_aprobado: i.total_aprobado, pendiente_legalizar: i.pendiente_legalizar, saldo: i.saldo });
        });
      } else {
        rows.push({ ingeniero: i.ingeniero_nombre, fecha_entrega: '', valor_entrega: '', descripcion: '', total_entregado: i.total_entregado, total_comprobado: i.total_comprobado, total_aprobado: i.total_aprobado, pendiente_legalizar: i.pendiente_legalizar, saldo: i.saldo });
      }
    });
    exportToExcel(rows, [
      { header: 'Ingeniero', value: r => r.ingeniero, width: 25 },
      { header: 'Fecha Entrega', value: r => r.fecha_entrega, width: 14 },
      { header: 'Valor Entrega', value: r => r.valor_entrega, width: 18 },
      { header: 'Descripción', value: r => r.descripcion, width: 30 },
      { header: 'Total Entregado', value: r => r.total_entregado, width: 18 },
      { header: 'Comprobado', value: r => r.total_comprobado, width: 18 },
      { header: 'Aprobado', value: r => r.total_aprobado, width: 18 },
      { header: 'Pend. Legalizar', value: r => r.pendiente_legalizar, width: 18 },
      { header: 'Saldo', value: r => r.saldo, width: 18 },
    ], `CajaMenor_${fechaDesde}_${fechaHasta}`);
  };

  return (
    <div>
      <div className="cont-filtros">
        <button className="cont-btn cont-btn-export" onClick={handleExport} disabled={!data?.items?.length}>
          Exportar Excel
        </button>
      </div>

      {loading && <div className="cont-loading">Cargando...</div>}

      {!loading && data && (
        <>
          <div className="cont-totales-bar">
            <div><span>Entregado:</span> <strong>{formatMoney(data.totales.total_entregado)}</strong></div>
            <div><span>Comprobado:</span> <strong>{formatMoney(data.totales.total_comprobado)}</strong></div>
            <div style={{ color: '#059669' }}><span>Aprobado:</span> <strong>{formatMoney(data.totales.total_aprobado)}</strong></div>
            <div style={{ color: '#dc2626' }}><span>Pend. Legalizar:</span> <strong>{formatMoney(data.totales.pendiente_legalizar)}</strong></div>
          </div>

          <div className="cont-table-wrap">
            <table className="cont-table">
              <thead>
                <tr>
                  <th></th><th>Ingeniero</th><th>Entregas</th><th>Entregado</th><th>Comprobado</th><th>Aprobado</th>
                  <th>Pend. Legalizar</th><th>Saldo</th>
                </tr>
              </thead>
              <tbody>
                {data.items.length === 0 ? (
                  <tr><td colSpan={8} className="cont-empty-td">Sin datos de caja menor</td></tr>
                ) : data.items.map(i => (
                  <React.Fragment key={i.ingeniero_id}>
                    <tr
                      onClick={() => setExpandedId(expandedId === i.ingeniero_id ? null : i.ingeniero_id)}
                      style={{ cursor: 'pointer' }}
                    >
                      <td style={{ width: 24, textAlign: 'center', fontSize: '0.8rem' }}>
                        {i.entregas?.length > 0 ? (expandedId === i.ingeniero_id ? '▼' : '▶') : ''}
                      </td>
                      <td><strong>{i.ingeniero_nombre}</strong></td>
                      <td className="num">{i.entregas?.length || 0}</td>
                      <td className="num">{formatMoney(i.total_entregado)}</td>
                      <td className="num">{formatMoney(i.total_comprobado)}</td>
                      <td className="num" style={{ color: '#059669' }}>{formatMoney(i.total_aprobado)}</td>
                      <td className="num" style={{ color: i.pendiente_legalizar > 0 ? '#dc2626' : '#059669', fontWeight: 600 }}>{formatMoney(i.pendiente_legalizar)}</td>
                      <td className="num" style={{ fontWeight: 700 }}>{formatMoney(i.saldo)}</td>
                    </tr>
                    {expandedId === i.ingeniero_id && i.entregas?.length > 0 && (
                      <tr><td colSpan={8} style={{ padding: 0 }}>
                        <div className="cont-pagos-detalle">
                          <strong>Detalle de entregas:</strong>
                          <table className="cont-table-inner">
                            <thead><tr><th>Fecha</th><th>Valor</th><th>Descripción</th></tr></thead>
                            <tbody>
                              {i.entregas.map(e => (
                                <tr key={e.id}>
                                  <td>{formatDate(e.fecha)}</td>
                                  <td className="num" style={{ fontWeight: 600 }}>{formatMoney(e.valor)}</td>
                                  <td>{e.descripcion || '-'}</td>
                                </tr>
                              ))}
                              <tr style={{ background: '#e2e8f0', fontWeight: 700 }}>
                                <td>TOTAL</td>
                                <td className="num">{formatMoney(i.total_entregado)}</td>
                                <td>{i.entregas.length} entrega(s)</td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </td></tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
